import type { FormEvent, RefObject } from 'react'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import InputBase from '@mui/material/InputBase'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { ArrowDown as ArrowDownIcon } from '@phosphor-icons/react/dist/ssr/ArrowDown'
import { ArrowUp as ArrowUpIcon } from '@phosphor-icons/react/dist/ssr/ArrowUp'
import { CornersOut as CornersOutIcon } from '@phosphor-icons/react/dist/ssr/CornersOut'
import { Microphone as MicrophoneIcon } from '@phosphor-icons/react/dist/ssr/Microphone'
import { Monitor as MonitorIcon } from '@phosphor-icons/react/dist/ssr/Monitor'
import { Stop as StopIcon } from '@phosphor-icons/react/dist/ssr/Stop'

import { stopAssistantSpeech } from '@/lib/assistant-speech'

import { micPulseSx, promptTextSx } from './chat-prompt-row'

export interface ChatComposerProps {
	/** True when the composer floats over the message list; false renders in flow (empty state). */
	floating: boolean
	input: string
	inputRef: RefObject<HTMLTextAreaElement>
	isExpanded: boolean
	isLoading: boolean
	isMobile: boolean
	contextLabel: string | null
	showContextLabel: boolean
	includePageContext: boolean
	onTogglePageContext: () => void
	interimText: string
	isListening: boolean
	speechSupported: boolean
	onToggleListening: () => void
	isReadingAloud: boolean
	isSpeechLoading: boolean
	showScrollDown: boolean
	onScrollToBottom: () => void
	onSubmit: (event?: FormEvent) => void | Promise<void>
	onInputChange: (value: string) => void
	onStopStreaming: () => void
}

export function ChatComposer({
	floating,
	input,
	inputRef,
	isExpanded,
	isLoading,
	isMobile,
	contextLabel,
	showContextLabel,
	includePageContext,
	onTogglePageContext,
	interimText,
	isListening,
	speechSupported,
	onToggleListening,
	isReadingAloud,
	isSpeechLoading,
	showScrollDown,
	onScrollToBottom,
	onSubmit,
	onInputChange,
	onStopStreaming,
}: ChatComposerProps) {
	const theme = useTheme()

	return (
		<Box
			component="form"
			onSubmit={onSubmit}
			sx={
				floating
					? {
							bottom: 0,
							left: 0,
							position: 'absolute',
							px: 2,
							pb: 'calc(16px + env(safe-area-inset-bottom))',
							right: 0,
						}
					: undefined
			}
		>
			<Box
				sx={{
					position: 'absolute',
					bottom: 'calc(100% + 8px)',
					left: '50%',
					transform: 'translateX(-50%)',
					pointerEvents: showScrollDown ? 'auto' : 'none',
				}}
			>
				<IconButton
					aria-hidden={!showScrollDown}
					aria-label={isLoading ? 'Response streaming below — scroll to latest' : 'Scroll to latest message'}
					onClick={onScrollToBottom}
					size="small"
					tabIndex={showScrollDown ? 0 : -1}
					sx={{
						// Compact horizontal pill while streaming; circle for the arrow.
						width: isLoading ? 'auto' : { xs: 36, sm: 32 },
						height: isLoading ? { xs: 26, sm: 24 } : { xs: 36, sm: 32 },
						px: isLoading ? 1.25 : 0,
						borderRadius: isLoading ? '999px' : '50%',
						// Glass surface
						bgcolor: 'color-mix(in srgb, var(--mui-palette-background-paper) 65%, transparent)',
						backdropFilter: 'blur(12px) saturate(1.4)',
						WebkitBackdropFilter: 'blur(12px) saturate(1.4)',
						border: '1px solid',
						borderColor: 'divider',
						boxShadow: '0 1px 4px rgba(0,0,0,0.08), 0 4px 12px rgba(0,0,0,0.08)',
						color: 'text.primary',
						// Toast open/close motion: rise + fade + cross-blur,
						// slower in (350ms) than out (250ms).
						opacity: showScrollDown ? 1 : 0,
						transform: showScrollDown ? 'translateY(0) scale(1)' : 'translateY(16px) scale(0.97)',
						filter: showScrollDown ? 'blur(0)' : 'blur(2px)',
						willChange: 'transform, opacity, filter',
						transition: `opacity ${showScrollDown ? 350 : 250}ms cubic-bezier(0.22, 1, 0.36, 1), transform ${showScrollDown ? 350 : 250}ms cubic-bezier(0.22, 1, 0.36, 1), filter ${
							showScrollDown ? 350 : 250
						}ms cubic-bezier(0.22, 1, 0.36, 1)`,
						'@media (prefers-reduced-motion: reduce)': { transition: 'none' },
						'&:hover': {
							bgcolor: 'color-mix(in srgb, var(--mui-palette-background-paper) 80%, transparent)',
						},
					}}
				>
					<Box
						component="span"
						// Remount on the streaming→done swap so the icon pops in.
						key={isLoading ? 'streaming' : 'done'}
						sx={{
							display: 'inline-flex',
							alignItems: 'center',
							animation: isLoading ? 'none' : 'scrollPillIconIn 200ms cubic-bezier(0.22, 1, 0.36, 1) both',
							'@keyframes scrollPillIconIn': {
								from: { opacity: 0, transform: 'scale(0.5)' },
								to: { opacity: 1, transform: 'scale(1)' },
							},
							'@media (prefers-reduced-motion: reduce)': { animation: 'none' },
						}}
					>
						{isLoading ? (
							<Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
								{[0, 1, 2].map((i) => (
									<Box
										component="span"
										key={i}
										sx={{
											width: 3.5,
											height: 3.5,
											borderRadius: '50%',
											bgcolor: 'text.primary',
											animation: `scrollPillDot 900ms cubic-bezier(0.22, 1, 0.36, 1) ${i * 140}ms infinite`,
											'@keyframes scrollPillDot': {
												'0%, 60%, 100%': { opacity: 0.35, transform: 'translateY(0)' },
												'30%': { opacity: 1, transform: 'translateY(-3px)' },
											},
											'@media (prefers-reduced-motion: reduce)': { animation: 'none', opacity: 1 },
										}}
									/>
								))}
							</Box>
						) : (
							<ArrowDownIcon size={17} weight="bold" />
						)}
					</Box>
				</IconButton>
			</Box>
			<Box
				sx={{
					...(isExpanded ? { maxWidth: 760, mx: 'auto' } : {}),
					// Top corners stay tighter; bottom matches the inner card
					// so its corners aren't clipped by overflow: hidden.
					borderRadius: '10px 10px 20px 20px',
					overflow: 'hidden',
					// Shadow only when the context strip renders — a transparent
					// wrapper would otherwise draw a faint ring around the input.
					// (showContextLabel implies !isMobile, so no breakpoint needed.)
					boxShadow: showContextLabel ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
					// action.hover is translucent — layer it over the input
					// surface so the floating context strip stays opaque.
					...(showContextLabel
						? {
								background: `linear-gradient(${theme.palette.action.hover}, ${theme.palette.action.hover}), ${
									theme.palette.mode === 'dark'
										? 'var(--mui-palette-background-default)'
										: 'var(--mui-palette-background-level1)'
								}`,
							}
						: {}),
				}}
			>
				{Boolean(contextLabel) && !isMobile ? (
					// Grid-rows collapse animates the strip in/out — open is slower
					// (250ms) than close (200ms) per the transitions.dev asymmetry.
					<Box
						sx={{
							display: 'grid',
							gridTemplateRows: showContextLabel ? '1fr' : '0fr',
							transition: `grid-template-rows ${showContextLabel ? 250 : 200}ms cubic-bezier(0.22, 1, 0.36, 1)`,
							'@media (prefers-reduced-motion: reduce)': { transition: 'none' },
						}}
					>
						<Box sx={{ minHeight: 0, overflow: 'hidden' }}>
							<Box
								sx={{
									opacity: showContextLabel ? 1 : 0,
									transform: showContextLabel ? 'none' : 'translateY(4px)',
									filter: showContextLabel ? 'blur(0)' : 'blur(2px)',
									transition: `opacity ${showContextLabel ? 250 : 200}ms cubic-bezier(0.22, 1, 0.36, 1), transform ${showContextLabel ? 250 : 200}ms cubic-bezier(0.22, 1, 0.36, 1), filter ${
										showContextLabel ? 250 : 200
									}ms cubic-bezier(0.22, 1, 0.36, 1)`,
									'@media (prefers-reduced-motion: reduce)': { transition: 'none' },
								}}
							>
								<Tooltip title="Your next question will include this context">
									<Box
										sx={{
											display: 'flex',
											alignItems: 'center',
											gap: 0.5,
											px: 1.5,
											py: 0.625,
											color: 'text.secondary',
										}}
									>
										<MonitorIcon size={12} weight="bold" />
										<Typography
											color="text.primary"
											noWrap
											sx={{ fontSize: '0.75rem', fontWeight: 500, lineHeight: 1.4 }}
											variant="caption"
										>
											{contextLabel}
										</Typography>
									</Box>
								</Tooltip>
							</Box>
						</Box>
					</Box>
				) : null}
				<Box
					sx={{
						bgcolor: 'background.default',
						border: '1px solid',
						borderColor: 'divider',
						borderRadius: '20px',
						display: 'flex',
						flexDirection: 'column',
					}}
				>
					{interimText ? (
						<Typography
							color="text.secondary"
							sx={{ px: 1.5, pb: 0.5, fontStyle: 'italic', fontSize: '0.75rem' }}
							variant="body2"
						>
							{interimText}…
						</Typography>
					) : null}
					{isReadingAloud ? (
						<Stack
							direction="row"
							spacing={0.5}
							sx={{ alignItems: 'center', justifyContent: 'space-between', px: 1.5, pt: 1, pb: 0.25 }}
						>
							<Typography color="text.secondary" sx={{ fontSize: '0.75rem' }} variant="body2">
								{isSpeechLoading ? 'Preparing audio…' : 'Reading aloud…'}
							</Typography>
							<Tooltip title="Stop reading">
								<IconButton aria-label="Stop reading" color="primary" onClick={stopAssistantSpeech} size="small">
									<StopIcon size={16} />
								</IconButton>
							</Tooltip>
						</Stack>
					) : null}
					<InputBase
						aria-label="Ask a question"
						fullWidth
						inputRef={inputRef}
						maxRows={4}
						multiline
						onChange={(e) => {
							onInputChange(e.target.value)
						}}
						onKeyDown={(e) => {
							if (e.key === 'Enter' && !e.shiftKey) {
								e.preventDefault()
								onSubmit()
							}
						}}
						placeholder="Ask a question…"
						sx={{
							...promptTextSx,
							px: 1.5,
							py: showContextLabel || interimText || isReadingAloud ? 0.75 : 1.25,
							'& .MuiInputBase-input': {
								...promptTextSx,
								fontSize: { xs: '1rem', sm: promptTextSx.fontSize },
								p: 0,
								'&::placeholder': { color: 'text.disabled' },
							},
						}}
						value={input}
					/>
					<Stack
						direction="row"
						spacing={0.5}
						sx={{ alignItems: 'center', justifyContent: 'flex-end', px: 1, pb: 1, pt: 0.5 }}
					>
						{/* Hidden on mobile — the panel is full-screen there so the
							screen context is already obvious. */}
						{!isMobile ? (
							<Tooltip
								title={
									includePageContext
										? 'Remove screen context from next question'
										: 'Include screen context in next question'
								}
							>
								<IconButton
									aria-label={includePageContext ? 'Remove screen context' : 'Add screen context'}
									aria-pressed={includePageContext}
									onClick={onTogglePageContext}
									size="small"
									sx={{
										width: { xs: 36, sm: 32 },
										height: { xs: 36, sm: 32 },
										borderRadius: '50%',
										color: 'text.secondary',
									}}
								>
									<CornersOutIcon size={18} weight="bold" />
								</IconButton>
							</Tooltip>
						) : null}
						{speechSupported ? (
							<Tooltip
								title={isListening ? 'Stop listening' : isReadingAloud ? 'Stop reading and listen' : 'Voice input'}
							>
								<IconButton
									aria-label={isListening ? 'Stop listening' : 'Voice input'}
									color={isListening ? 'error' : 'default'}
									onClick={onToggleListening}
									size="small"
									sx={{
										width: { xs: 36, sm: 32 },
										height: { xs: 36, sm: 32 },
										borderRadius: '50%',
										...(isListening ? micPulseSx : {}),
									}}
								>
									<MicrophoneIcon size={isMobile ? 18 : 16} weight="bold" />
								</IconButton>
							</Tooltip>
						) : null}
						<IconButton
							aria-label={isLoading ? 'Stop generating' : 'Send message'}
							disabled={!isLoading && !input.trim()}
							onClick={() => {
								if (isLoading) onStopStreaming()
								else void onSubmit()
							}}
							size="small"
							sx={{
								width: { xs: 36, sm: 32 },
								height: { xs: 36, sm: 32 },
								borderRadius: '50%',
								bgcolor: 'primary.main',
								color: 'primary.contrastText',
								'&:hover': { bgcolor: 'primary.dark' },
								'&.Mui-disabled': {
									bgcolor: 'action.disabledBackground',
									color: 'action.disabled',
								},
							}}
							type="button"
						>
							{isLoading ? (
								<StopIcon size={isMobile ? 16 : 14} weight="fill" />
							) : (
								<ArrowUpIcon size={isMobile ? 18 : 16} weight="bold" />
							)}
						</IconButton>
					</Stack>
				</Box>
			</Box>
		</Box>
	)
}
